/**
 * Sidebar - Smart Navigation Panel
 * - Filter theo permission qua useSidebarNavigation()
 * - Ho tro multi-role union
 * - Auto-expand group dang active
 * - Desktop collapsed/expanded va mobile drawer
 */
import { useState, useCallback, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { brandConfig } from '@/config/brand.config';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { useAuth } from '@/core/auth/AuthContext';
import { useAdminRolePreviewContext } from '@/core/roles/AdminRolePreviewContext';
import { useSidebarNavigation } from '@/core/permissions/hooks/useSidebarNavigation';
import { Button } from '@/components/ui';
import { useAI } from '@/core/ai/AIContext';
import { useCompanyBranding } from '@/shared/hooks/useCompanyBranding';
import type { SidebarRenderItem } from '@/core/permissions/hooks/useSidebarNavigation';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function SideNavItem({ item, collapsed }: { item: SidebarRenderItem; collapsed: boolean }) {
  return (
    <NavLink
      to={item.path}
      title={collapsed ? item.label : undefined}
      className={`
        relative flex min-h-9 items-center ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'} rounded-md py-1.5 sm:text-sm text-xs
        transition-all duration-150 whitespace-nowrap group border-l-4
        ${item.isActive
          ? 'bg-brand-50 text-brand-700 border-brand-600'
          : 'text-slate-500 border-transparent hover:bg-slate-50 hover:text-slate-800'
        }
      `}
    >
      <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors ${item.isActive ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-400 group-hover:bg-slate-50 group-hover:text-brand-600'
        }`}>
        <i className={`${item.icon} text-base`} />
      </span>

      {!collapsed && (
        <>
          <span className="flex-1 truncate">{item.label}</span>
          {item.badge && (
            <span className={`ml-auto min-w-5 rounded-full px-1.5 py-0.5 text-center text-xs ${item.isActive ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'
              }`}>
              {item.badge}
            </span>
          )}
        </>
      )}

      {collapsed && item.badge && (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-brand-500 ring-2 ring-white" />
      )}
    </NavLink>
  );
}

function SidebarSkeleton() {
  return (
    <div className="flex-1 space-y-3 overflow-y-auto px-3 py-3 animate-pulse">
      {[1, 2, 3].map(group => (
        <div key={group}>
          <div className="mx-2 mb-2 h-3 w-20 rounded bg-slate-100" />
          {[1, 2, 3].map(item => (
            <div key={item} className="mb-0.5 h-9 rounded-md bg-slate-50" />
          ))}
        </div>
      ))}
    </div>
  );
}

function AssistantCard({ collapsed }: { collapsed: boolean }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { openChat, maxLimit } = useAI();

  if (collapsed || maxLimit === 0) return null;

  return (
    <div className="hidden lg:block mx-3 mb-3 rounded-md border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-slate-50 p-3 shadow-sm transition-all duration-300">
      <div
        className="flex items-center gap-2 cursor-pointer select-none"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-brand-600 shadow-sm shrink-0 border border-slate-100">
          <i className="ri-sparkling-2-line text-base" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate sm:text-sm text-xs text-slate-800 font-medium">Trợ lý Landscape</p>
          <p className="truncate text-[10px] text-slate-500">Trung tâm điều hành AI</p>
        </div>
        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-slate-400`} />
      </div>

      {isExpanded && (
        <div className="mt-3 animate-fade-in">
          <p className="mb-3 text-[11px] leading-4 text-slate-500">
            Kết nối dữ liệu và hỗ trợ vận hành thông minh 24/7.
          </p>
          <Button
            className="w-full h-8 bg-brand-600 text-white rounded-md text-xs hover:bg-brand-700 shadow-sm"
            onClick={() => openChat()}
          >
            Bắt đầu chat
          </Button>
        </div>
      )}
    </div>
  );
}

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { user, isLoading } = useCurrentUser();
  const { isPreviewMode } = useAdminRolePreviewContext();
  const { isImpersonating } = useAuth();

  const {
    groups,
    pinnedItems,
    activeGroupId,
    roleLabel,
    roleColor,
    isLoading: navLoading,
  } = useSidebarNavigation();

  const getInitialExpanded = useCallback((): Set<string> => {
    try {
      const raw = sessionStorage.getItem('sidebar_expanded_groups');
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {
      // ignore storage errors
    }
    return new Set(['overview', 'construction', 'quality-approval', 'maintenance', 'system']);
  }, []);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(getInitialExpanded);

  useEffect(() => {
    if (activeGroupId && !expandedGroups.has(activeGroupId)) {
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.add(activeGroupId);
        try {
          sessionStorage.setItem('sidebar_expanded_groups', JSON.stringify(Array.from(next)));
        } catch {
          // ignore storage errors
        }
        return next;
      });
    }
  }, [activeGroupId, expandedGroups]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        sessionStorage.setItem('sidebar_expanded_groups', JSON.stringify(Array.from(next)));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const showSkeleton = (isLoading && user.id === '') || navLoading;
  const branding = useCompanyBranding();

  const sidebarContent = (
    <div className="flex h-full flex-col bg-white">
      <div className={`flex h-16 flex-shrink-0 items-center border-b border-slate-100 ${collapsed ? 'justify-center px-0' : 'gap-3 px-4'
        }`}>
        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md ${branding.logo_path ? 'bg-white border border-slate-200' : 'bg-gradient-to-br from-brand-600 to-blue-700 text-white shadow-sm'}`}>
          {branding.logo_path ? (
            <img src={branding.logo_path} alt="Logo" className="h-full w-full object-contain rounded-md" />
          ) : (
            <i className={`${brandConfig.logo.icon} text-xl`} />
          )}
        </div>

        {!collapsed && (
          <div className="min-w-0 flex-1">
            <p className="truncate sm:text-sm text-xs text-slate-900 font-semibold">{branding.name}</p>
            <div className="mt-1 flex items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-xs border ${roleColor.bg} ${roleColor.text} ${roleColor.border}`}>
                {roleLabel}
              </span>
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="icon"
          onClick={onMobileClose}
          className="ml-auto lg:hidden text-slate-400"
          aria-label="Đóng menu"
        >
          <i className="ri-close-line text-base" />
        </Button>
      </div>

      {isPreviewMode && !collapsed && (
        <div className="mx-3 mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <div className="flex items-center gap-2">
            <i className="ri-eye-line text-sm text-amber-600" />
            <span className="truncate text-xs text-amber-700">
              Preview: {roleLabel}
            </span>
          </div>
        </div>
      )}

      {isPreviewMode && collapsed && (
        <div className="mx-2 mb-2 flex justify-center">
          <span className="h-2 w-2 rounded-full bg-amber-400" />
        </div>
      )}

      {showSkeleton ? (
        <SidebarSkeleton />
      ) : (
        <nav className={`min-h-0 flex-1 space-y-0 overflow-y-auto py-2 ${collapsed ? 'px-2 scrollbar-hide' : 'px-3'}`}>
          {groups.map(group => (
            <div key={group.id} className="mb-1">
              {!collapsed && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="mb-1 flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-1.5 sm:text-sm text-xs tracking-wide text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800"
                >
                  <span>{group.label}</span>
                  <i className={`${expandedGroups.has(group.id) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-sm`} />
                </button>
              )}

              {collapsed && <div className="mx-2 mb-1 h-px bg-slate-100" />}

              {(collapsed || expandedGroups.has(group.id)) && (
                <div className="space-y-0">
                  {group.items.map(item => (
                    <SideNavItem key={item.id} item={item} collapsed={collapsed} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      )}

      {!showSkeleton && pinnedItems.length > 0 && (
        <div className={`flex-shrink-0 space-y-1 border-t border-slate-100 py-2 ${collapsed ? 'px-2' : 'px-3'}`}>
          {pinnedItems.map(item => (
            <SideNavItem key={item.id} item={item} collapsed={collapsed} />
          ))}
        </div>
      )}

      <AssistantCard collapsed={collapsed} />

      <div className="hidden flex-shrink-0 border-t border-slate-100 p-2 lg:block">
        <Button
          variant="ghost"
          onClick={onToggle}
          className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-md py-2 sm:text-sm text-xs text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 ${collapsed ? 'px-0' : 'px-3'}`}
          title={collapsed ? 'Cố định' : 'Thu gọn'}
        >
          <i className={`ri-${collapsed ? 'pushpin-2' : 'menu-fold'}-line text-base`} />
          {!collapsed && <span className="sm:text-sm text-xs">{collapsed ? 'Cố định' : 'Thu gọn'}</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <aside
        className={`
          sticky top-0 z-[100] hidden flex-col lg:flex
          h-screen flex-shrink-0
          ${collapsed ? 'w-16' : 'w-56'}
          transition-all duration-300 ease-in-out
          border-r border-slate-100 bg-white shadow-sm
        `}
      >
        {sidebarContent}
      </aside>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/35 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed left-0 top-0 z-[60] flex h-full w-56 flex-col lg:hidden
          bg-white transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
